import React from 'react';
import styled from 'styled-components';
import { combineClassNames } from '../../utils/base';
import generateComponentStyles from '../../utils/styleHelper';

// Every `component` the platform knows: the ones this renderer draws, plus `button` and
// `divider`, which only the editor draws and which render as nothing here. Mirrors
// `isUnknownComponent` in the editor (fractal-fe `UnknownComponentChunk`).
const KNOWN_COMPONENTS = new Set([
  'screen', 'container', 'table',
  'text', 'input', 'select', 'checkbox', 'radio', 'switcher', 'chart',
  'image', 'separator', 'button', 'divider',
]);

export const isUnknownComponent = (component: unknown): boolean => (
  typeof component !== 'string' || !KNOWN_COMPONENTS.has(component)
);

const StyledNode = styled.div<{
  $shape?: any;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
  ${(props) => generateComponentStyles(props.$shape, undefined, undefined, props.$parentDisplay, undefined, props.$parentFlexDirection)}
`;

// The editor's placeholder, which is the mobile canvas's (`paintPlaceholder`).
const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 4px 8px;
  border: 1px dashed #b0b0b8;
  border-radius: 8px;
  background: rgba(176, 176, 184, 0.1);
`;

const Label = styled.span`
  overflow: hidden;
  color: #b0b0b8;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type UnknownComponentProps = {
  id?: string;
  component?: unknown;
  style?: any;
  parentStyle?: any;
};

// FE-4974: the API accepts any `component` string, and a type nothing here can draw rendered
// as nothing at all. It is drawn as the placeholder the editor shows, in the node's own box
// (its size and placement). Nothing the node holds is rendered: its children and value mean
// nothing without the component that would lay them out.
const UnknownComponent: React.FC<UnknownComponentProps> = ({
  id, component, style, parentStyle,
}) => (
  <StyledNode
    id={id}
    data-id={id}
    data-component="unknown"
    $shape={style}
    $parentDisplay={parentStyle?.display}
    $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
    className={combineClassNames(style?.class)}
  >
    <Placeholder>
      <Label>
        {typeof component === 'string' && component
          ? `Unknown component: ${component}`
          : 'Unknown component'}
      </Label>
    </Placeholder>
  </StyledNode>
);

export default UnknownComponent;
